import React from 'react'
import { Button, FormItem } from 'vnetwork-ui'
import DepartmentSelect from '@/components/Form/Shared/DepartmentSelect'
import PositionSelect from '@/components/Form/Shared/PositionSelect'
import {
    EMPLOYEE_CONTRACT_TYPE,
    EMPLOYEE_SENIORITY_LEVEL,
    EMPLOYEE_EXTERNAL_TITLE,
} from '@/modules/Employee/constants'
import {
    createEmployeeTmp,
    getAllDepartments,
    getAllPositionsByDepartmentId,
} from '@/modules/Employee/services'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useSearchParams } from 'react-router-dom'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'

// Get allowed email domains from ENV (comma-separated)
// Example: APP_ALLOWED_EMAIL_DOMAINS=vnetwork.vn,company.com
const getAllowedEmailDomains = (): string[] => {
    const domains = import.meta.env.APP_ALLOWED_EMAIL_DOMAINS
    if (!domains) return []
    return domains.split(',').map((d: string) => d.trim().toLowerCase())
}

// Validate email domain
const validateEmailDomain = (email: string): boolean => {
    const allowedDomains = getAllowedEmailDomains()
    if (allowedDomains.length === 0) return true // No restriction if not configured

    const emailDomain = email.split('@')[1]?.toLowerCase()
    return allowedDomains.some((domain) => emailDomain === domain)
}

export interface CreateEmployeeTmpForm {
    employee_name: string
    department_id: number
    position_id: number
    employee_contract_type: string
    employee_seniority_level: string
    employee_external_title: string
    employee_email_company: string
}

const schema = yup.object().shape({
    department_id: yup
        .number()
        .min(1, 'Chưa chọn phòng ban')
        .required('Chưa chọn phòng ban'),
    position_id: yup
        .number()
        .min(1, 'Chưa chọn vị trí')
        .required('Chưa chọn vị trí'),
    employee_contract_type: yup.string().required('Chưa chọn loại nhân viên'),
    employee_seniority_level: yup.string().required('Chưa chọn thâm niên'),
    employee_email_company: yup
        .string()
        .required('Chưa nhập email')
        .email('Email không hợp lệ')
        .test('email-domain', function (value) {
            if (!value) return true // Already handled by required

            const allowedDomains = getAllowedEmailDomains()
            if (allowedDomains.length === 0) return true // No restriction

            const isValid = validateEmailDomain(value)
            if (!isValid) {
                const domainsList = allowedDomains.join(', ')
                return this.createError({
                    message: `Email phải có đuôi: ${domainsList}`,
                })
            }
            return true
        }),
    employee_name: yup.string().required('Chưa nhập họ và tên'),
})

interface CreateEmployeeTmpProps {
    onSuccess: (data: Awaited<ReturnType<typeof createEmployeeTmp>>) => void
}

const CreateEmployeeTmp: React.FC<CreateEmployeeTmpProps> = ({ onSuccess }) => {
    const { control, handleSubmit, setValue } = useForm<CreateEmployeeTmpForm>({
        defaultValues: {
            department_id: 0,
            position_id: 0,
            employee_contract_type: '',
            employee_seniority_level: '',
            employee_external_title: '',
            employee_email_company: '',
            employee_name: '',
        },
        resolver: yupResolver(schema),
    })

    const { t } = useTranslation()
    const [searchParams] = useSearchParams()

    const departmentId = useWatch({
        control,
        name: 'department_id',
    })
    const positionId = useWatch({
        control,
        name: 'position_id',
    })

    const { data: departments } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.DEPARTMENTS],
        queryFn: getAllDepartments,
    })
    const { data: positions } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.POSITIONS, departmentId],
        queryFn: () => getAllPositionsByDepartmentId(departmentId),
        enabled: !!departmentId,
    })

    const selectedDepartment = departments?.results?.find(
        (department) => department.department_id === departmentId
    )
    const selectedPosition = positions?.results?.find(
        (position) => position.position_id === positionId
    )
    const departmentGroupPaths =
        selectedDepartment?.keycloak_default_group_paths ??
        (selectedDepartment?.keycloak_default_group_path
            ? [selectedDepartment.keycloak_default_group_path]
            : [])
    const positionGroupPaths =
        selectedPosition?.keycloak_default_group_paths ??
        (selectedPosition?.keycloak_default_group_path
            ? [selectedPosition.keycloak_default_group_path]
            : [])
    const defaultGroupPaths = Array.from(
        new Set([...departmentGroupPaths, ...positionGroupPaths])
    )

    // Prefill from query params (e.g., from CV)
    React.useEffect(() => {
        const name = searchParams.get('name')
        const email = searchParams.get('email')
        const departmentIdParam = searchParams.get('department_id')
        const employeeTypeParam = searchParams.get('employee_type')

        if (name) setValue('employee_name', name)
        if (email) setValue('employee_email_company', email)
        if (departmentIdParam && !isNaN(Number(departmentIdParam))) {
            setValue('department_id', Number(departmentIdParam))
        }
        if (employeeTypeParam)
            setValue('employee_contract_type', employeeTypeParam)
    }, [searchParams, setValue])

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: createEmployeeTmp,
        },
        {
            reactQueryKeys: QUERY_KEYS.EMPLOYEES,
            successMessage: 'Success',
            errorMessage: 'error-occurred',
            onSuccess: (data) => {
                onSuccess(data)
            },
        }
    )

    const handleCreateEmployee = (values: CreateEmployeeTmpForm) => {
        mutateAsync(values)
    }

    return (
        <form
            onSubmit={handleSubmit(handleCreateEmployee)}
            data-tour="employee-form"
            className="min-h-[290px] pb-2"
        >
            <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-4">
                <div data-tour="employee-name">
                    <FormItem
                        control={control}
                        name="employee_name"
                        type="textfield"
                        placeholder={t('employee_name')}
                        label={t('employee_name')}
                    />
                </div>

                <div data-tour="employee-department">
                    <DepartmentSelect
                        control={control}
                        name="department_id"
                        onChangeAfterHookForm={() => setValue('position_id', 0)}
                    />
                </div>

                <div data-tour="employee-position">
                    <PositionSelect
                        control={control}
                        departmentId={departmentId}
                        name="position_id"
                    />
                </div>

                <div data-tour="employee-type">
                    <FormItem
                        control={control}
                        name="employee_contract_type"
                        type="select"
                        label={t('employee_contract_type')}
                        placeholder={t('employee_contract_type_placeholder')}
                        options={EMPLOYEE_CONTRACT_TYPE(t)}
                        allowClear={false}
                    />
                </div>

                <div data-tour="employee-seniority">
                    <FormItem
                        control={control}
                        name="employee_seniority_level"
                        type="select"
                        label={t('employee_seniority_level')}
                        placeholder={t('employee_seniority_level_placeholder')}
                        options={EMPLOYEE_SENIORITY_LEVEL}
                        allowClear={false}
                    />
                </div>

                <div data-tour="employee-external-title">
                    <FormItem
                        control={control}
                        name="employee_external_title"
                        type="select"
                        label={t('employee_external_title')}
                        placeholder={t('employee_external_title')}
                        options={EMPLOYEE_EXTERNAL_TITLE}
                    />
                </div>

                <div data-tour="employee-email">
                    <FormItem
                        control={control}
                        name="employee_email_company"
                        type="textfield"
                        label={t('employee_email_company')}
                        placeholder="an.nguyen@vnetwork.vn"
                        // disabled
                    />
                </div>

                {!!departmentId && (
                    <div className="lg:col-span-3 md:col-span-2 rounded-md border border-gray-200 bg-gray-50 p-3">
                        {departmentGroupPaths.length > 0 ? (
                            <>
                                <div className="text-sm font-medium text-gray-800">
                                    Group mặc định sẽ gán khi kích hoạt
                                </div>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {defaultGroupPaths.map((path) => (
                                        <span
                                            key={path}
                                            className="rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-700"
                                        >
                                            {path}
                                        </span>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="text-sm text-amber-700">
                                Phòng ban chưa được cấu hình Keycloak group mặc
                                định. Nhân viên vẫn có thể được tạo tạm nhưng
                                chưa thể kích hoạt tài khoản.
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div data-tour="employee-submit">
                <Button
                    disabled={isPending}
                    loading={isPending}
                    className="mt-4"
                >
                    {t('create')}
                </Button>
            </div>
        </form>
    )
}

export default CreateEmployeeTmp
